// RegularisationAdminPage — admin inbox for attendance-regularisation requests
// (Phase A1). Approve/reject employee requests, or raise + approve one on behalf
// of an employee in a single step. Approval writes the attendance row via the
// sanctioned backend writer (source:"regularisation", wifiVerified:false) — this
// page only drives /api/attendance/regularisation/*; it changes no gate or
// attendance logic.
//
// Mirrors WfhAdminPage.jsx: status filter + table + per-row actions.

import React, { useEffect, useState, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, REQUEST_TONES } from "../../Components/ui";
import { Backdrop, Modal, Header, Body, Footer, Field, inputStyle, btnPrimary as mBtnPrimary, btnGhost as mBtnGhost } from "../Leaves/ApplyLeaveModal";

// Shared approval-lifecycle tones (see Components/ui/statusTones.js). Was a
// private copy of four hardcoded light-theme pairs, duplicated across five
// pages and unreadable-looking in dark mode.
const STATUS_STYLE = REQUEST_TONES;

const inputSel = { border: "1px solid var(--border-default)", borderRadius: 8, padding: "6px 10px", fontSize: 13, background: "var(--bg-surface)", color: "var(--text-primary)" };
const btnApprove = { background: "var(--fill-success)", color: "white", padding: "5px 12px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" };
const btnReject  = { background: "var(--fill-danger)", color: "white", padding: "5px 12px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" };
const btnPrimary = { background: "var(--accent-primary)", color: "white", border: "none", padding: "9px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" };

function staffName(s) {
  if (!s) return "—";
  return `${s.firstName || ""} ${s.lastName || ""}`.trim() || s.username || s.email || "—";
}
function fmtDate(d) { try { return new Date(d).toISOString().slice(0, 10); } catch { return "—"; } }
function fmtTime(t) { return t ? String(t).slice(0, 5) : "—"; }

export default function RegularisationAdminPage() {
  const toast = useToast();
  useShellHeader({ title: "Attendance Regularisation", breadcrumb: "Admin · Regularisation inbox" }, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [onBehalf, setOnBehalf] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api(`/attendance/regularisation${status ? `?status=${status}` : ""}`);
      setRows(Array.isArray(r) ? r : (r.regularisations || r.rows || []));
    } catch (e) {
      toast({ title: e.message, status: "error" });
    } finally {
      setLoading(false);
    }
  }, [status, toast]);

  useEffect(() => { load(); }, [load]);

  async function decide(r, decision) {
    const promptMsg = decision === "approved"
      ? "Approval note (optional):"
      : "Why are you rejecting this? (optional)";
    const note = window.prompt(promptMsg);
    if (note === null) return; // cancelled the prompt
    try {
      await api(`/attendance/regularisation/${r._id}/decision`, {
        method: "POST",
        body: JSON.stringify({ decision, adminNote: note || "" }),
      });
      toast({
        title: decision === "approved" ? "Approved — attendance recorded." : "Request rejected.",
        status: "success",
      });
      load();
    } catch (e) {
      toast({ title: e.data?.message || e.message, status: "error" });
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>Attendance Regularisation</h2>
          <div style={{ fontSize: 13, color: "var(--text-tertiary)", marginTop: 4, maxWidth: 640 }}>
            Approve a missed-attendance request to record that day as a normal paid day (marked admin-regularised, never an office-WiFi punch). If a real check-in already exists for the date, edit that attendance row instead.
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <select style={inputSel} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button style={btnPrimary} onClick={() => setOnBehalf(true)}>+ Mark on behalf</button>
        </div>
      </div>

      <Card padding="none">
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead><tr>
              {["Staff", "Date", "In", "Out", "Reason", "Raised by", "Status", "Admin Note", "Submitted", "Actions"].map((h) => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={{ ...tdBase, textAlign: "center", color: "var(--text-tertiary)" }}>Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={10} style={{ ...tdBase, textAlign: "center", color: "var(--text-tertiary)", padding: "24px 12px" }}>No regularisation requests {status ? `with status "${status}"` : ""}.</td></tr>
              ) : rows.map((r) => {
                const s = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
                return (
                  <tr key={r._id} style={{ borderTop: "1px solid var(--border-subtle)" }}>
                    <td style={tdBase}>
                      <div style={{ fontWeight: 500 }}>{staffName(r.staff)}</div>
                      <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{r.staff?.email || ""}</div>
                    </td>
                    <td className="tabular" style={tdBase}>{fmtDate(r.workDate)}</td>
                    <td className="tabular" style={tdBase}>{fmtTime(r.proposedCheckIn)}</td>
                    <td className="tabular" style={tdBase}>{fmtTime(r.proposedCheckOut)}</td>
                    <td style={{ ...tdBase, maxWidth: 300, color: "var(--text-secondary)" }}>{r.reason || "—"}</td>
                    <td style={{ ...tdBase, color: "var(--text-tertiary)", fontSize: 12 }}>{r.raisedBy === "admin" ? "Admin" : "Employee"}</td>
                    <td style={tdBase}>
                      <span style={{ background: s.bg, color: s.co, padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>{r.status}</span>
                    </td>
                    <td style={{ ...tdBase, color: "var(--text-tertiary)", fontSize: 12, maxWidth: 220 }}>{r.adminNote || "—"}</td>
                    <td className="tabular" style={tdBase}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "—"}</td>
                    <td style={tdBase}>
                      {r.status === "pending" ? (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button style={btnApprove} onClick={() => decide(r, "approved")}>Approve</button>
                          <button style={btnReject} onClick={() => decide(r, "rejected")}>Reject</button>
                        </div>
                      ) : (
                        <span style={{ color: "var(--text-tertiary)" }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <OnBehalfModal
        open={onBehalf}
        onClose={() => setOnBehalf(false)}
        onDone={() => { setOnBehalf(false); load(); }}
      />
    </div>
  );
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function toHHMMSS(t) { if (!t) return undefined; return t.length === 5 ? `${t}:00` : t; }

// Admin raises + approves a regularisation in one step. The server records the
// attendance row immediately (source:"regularisation"); it refuses if a real
// check-in already exists for the date.
function OnBehalfModal({ open, onClose, onDone }) {
  const toast = useToast();
  const [staffList, setStaffList] = useState([]);
  const [staffId, setStaffId] = useState("");
  const [workDate, setWorkDate] = useState("");
  const [checkIn, setCheckIn] = useState("10:00");
  const [checkOut, setCheckOut] = useState("19:00");
  const [reason, setReason] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const d = await api("/hr/staff");
        setStaffList(d.staff || d.rows || []);
      } catch (e) { toast({ title: e.message, status: "error" }); }
    })();
  }, [open, toast]);

  if (!open) return null;

  async function submit() {
    if (!staffId || !workDate || !reason.trim()) {
      toast({ title: "Employee, date and reason are required.", status: "warning" });
      return;
    }
    setBusy(true);
    try {
      await api("/attendance/regularisation/admin", {
        method: "POST",
        body: JSON.stringify({
          staffId,
          workDate,
          proposedCheckIn: toHHMMSS(checkIn),
          proposedCheckOut: toHHMMSS(checkOut),
          reason,
          adminNote,
        }),
      });
      toast({ title: "Attendance regularised and recorded.", status: "success" });
      setStaffId(""); setWorkDate(""); setCheckIn("10:00"); setCheckOut("19:00"); setReason(""); setAdminNote("");
      onDone && onDone();
    } catch (e) {
      toast({ title: e.data?.message || e.message, status: "error" });
    } finally { setBusy(false); }
  }

  return (
    <Backdrop onClose={onClose}>
      <Modal>
        <Header title="Mark attendance on behalf" onClose={onClose} />
        <Body>
          <Field label="EMPLOYEE *">
            <select value={staffId} onChange={(e) => setStaffId(e.target.value)} style={inputStyle}>
              <option value="">Select an employee…</option>
              {staffList.map((u) => (
                <option key={u._id} value={u._id}>{staffName(u)}{u.email ? ` · ${u.email}` : ""}</option>
              ))}
            </select>
          </Field>
          <Field label="MISSED DATE *">
            <input type="date" value={workDate} max={todayIso()} onChange={(e) => setWorkDate(e.target.value)} style={inputStyle} />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="CHECK-IN"><input type="time" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} style={inputStyle} /></Field>
            <Field label="CHECK-OUT"><input type="time" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} style={inputStyle} /></Field>
          </div>
          <Field label="REASON *">
            <textarea
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="What happened on this day (e.g. WiFi outage, verified they worked)."
              style={{ ...inputStyle, minHeight: 72, resize: "vertical" }}
            />
          </Field>
          <Field label="ADMIN NOTE (optional)">
            <input value={adminNote} onChange={(e) => setAdminNote(e.target.value)} style={inputStyle} placeholder="Internal note recorded with the approval." />
          </Field>
          <div style={{
            background: "var(--tone-warning-bg)", border: "1px solid var(--tone-warning-br)",
            borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "var(--tone-warning-fg)",
          }}>
            This records the day immediately as a paid, admin-regularised attendance row. It will be refused if a real check-in already exists for that date.
          </div>
        </Body>
        <Footer>
          <button onClick={onClose} style={mBtnGhost} disabled={busy}>Cancel</button>
          <button onClick={submit} style={mBtnPrimary} disabled={busy}>{busy ? "Saving…" : "Record attendance"}</button>
        </Footer>
      </Modal>
    </Backdrop>
  );
}

const thStyle = {
  textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 600,
  textTransform: "uppercase", letterSpacing: "0.06em",
  color: "var(--text-tertiary)", background: "var(--bg-subtle)",
  borderBottom: "1px solid var(--border-subtle)",
};
const tdBase = { padding: "10px 14px", color: "var(--text-primary)", verticalAlign: "top" };
